import { PayableInboxItem } from "@/Billing/Api/Inbox/Dto/Response/BillingInboxItem";
import { LoadPayableInboxResponse } from "@/Billing/Api/Inbox/Dto/Response/LoadPayableInboxResponse";
import { PayableComposite } from "@/Billing/Models/PayableComposite";
import { PayableInboxFilterSettings } from "@/Billing/Models/Settings/PayableInboxFilterSettings";
import { PayableInboxModel, PayableInboxSums } from "@/Billing/Models/UI/PaybleInboxModel";
import { AsyncAction } from "@/Common/Api/AsyncActions/AsyncAction";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { Guid } from "@/Common/Models/Guid";
import { PaginationSettings } from "@/Common/Models/PaginationSettings";
import { PayableInboxSortOrderType, PrivateBill_BilledTo } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { Server } from "@/Common/Operations/operations.server";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { BillingStore } from "../BillingStore";

class Actions extends AsyncAction {
    private _billingStore: BillingStore;

    private get billingStore(): BillingStore {
        if (!this._billingStore)
            this._billingStore = billingStore;

        return this._billingStore;
    }

    private get model(): PayableInboxModel {
        return this.billingStore.payableInboxModel;
    }

    constructor(billingStore: BillingStore) {
        super();
        this._billingStore = billingStore;
    }

    public async loadPayableInboxItems(
        filterSetting?: PayableInboxFilterSettings
    ): Promise<LoadPayableInboxResponse> {
        const filter = filterSetting ?? this.model.filterSettings;

        const response = await this.postAsync<LoadPayableInboxResponse>(Server.Myle_LoadPayableBills, { filter });

        const sums = new PayableInboxSums({
            SubtotalSum: Math.round((response.SubtotalSum + 0.00001) * 100) / 100,
            DiscountSum: Math.round((response.DiscountSum + 0.00001) * 100) / 100,
            GSTSum: Math.round((response.GSTSum + 0.00001) * 100) / 100,
            QSTSum: Math.round((response.QSTSum + 0.00001) * 100) / 100,
            HSTSum: Math.round((response.HSTSum + 0.00001) * 100) / 100,
            GenericTaxSum: Math.round((response.GenericTaxSum + 0.00001) * 100) / 100,
            TotalBillSum: Math.round((response.TotalBillSum + 0.00001) * 100) / 100
        });

        const items = this.mapResultsToPayableInboxItems(response.Results);
        filter.Pagination.Offset = response.Offset;
        filter.Pagination.TotalCount = response.TotalQueryRows;

        this.dispatchLocal({
            data: items,
            sums: sums,
            filterSettings: filter
        });

        return response;
    }

    public async saveFilterSettings(filterSettings: PayableInboxFilterSettings, onSuccess: () => void, isSaveSettingChecked: boolean): Promise<void> {
        if (isSaveSettingChecked) {
            this.saveUserSettingsAsync(filterSettings);
        }

        const response = await this.loadPayableInboxItems(filterSettings);

        if (!response.Errors.any())
            onSuccess();
    }

    public changePage(nextPageNumber: number): void {
        const nextOffset = (nextPageNumber - 1) * this.model.filterSettings.Pagination.PageSize;
        const nextPaginationSettings: PaginationSettings<PayableInboxSortOrderType> = {
            ...this.model.filterSettings.Pagination,
            Offset: nextOffset
        };

        this.loadPayableInboxItems({ ...this.model.filterSettings, Pagination: nextPaginationSettings });
    }

    private dispatchLocal(stateChanges: Partial<PayableInboxModel>): void {
        sessionStore.dispatch(LocalOperations.Billing.Payable_LoadPaybleInboxItems, { stateChanges });
    }

    private mapResultsToPayableInboxItems(Results: PayableComposite[]): PayableInboxItem[] {
        return Results.map(p => new PayableInboxItem({
            PrivateBillId: p.PrivateBillId,

            CreationDate: p.BillDate,
            VisitDate: p.VisitDate,

            Number: p.Number,
            Provider: this.getClinicianInfo(p.EmployeeId),
            Site: cacheStore.GetSiteById(p.SiteId)?.Model?.ClinicalName,

            Patient: NameHelper.formatFullNameForHasPatientName(p),
            PatientId: p.PatientId,
            PatientContactId: p.PatientContactId,
            HealthCard: p.PatientNAM,

            BilledBy: this.getClinicianInfo(p.BilledBy),
            BilledTo: p.BilledTo === PrivateBill_BilledTo.ThirdParty ? p.RecipientName : cacheStore.GetString("PatientLabel"),

            SubTotal: Math.round((p.Subtotal + 0.00001) * 100) / 100,
            Discount: Math.round((p.Discount + 0.00001) * 100) / 100,

            GST: Math.round((p.TotalGst + 0.00001) * 100) / 100,
            QST: Math.round((p.TotalQst + 0.00001) * 100) / 100,

            TotalDue: Math.round((p.TotalBill + 0.00001) * 100) / 100
        }));
    }

    private getClinicianInfo(Id: Guid): string {
        const employee = cacheStore.GetEmployeeByContactFacetId(Id);
        if (!employee)
            return null;

        const employeeTitle = cacheStore.GetLocalizedValueByLookupId(employee.PrefixLookupId);

        return `${employeeTitle} ${employee.LastName}, ${employee.FirstName}${(employee.FirstLicenceNumber ? " (" + employee.FirstLicenceNumber + ")" : "")}`;
    }

    private async saveUserSettingsAsync(filterSetting: PayableInboxFilterSettings): Promise<void> {
        const userSettings = new UserSettings();
        userSettings.PayableInboxFilterSettings = filterSetting;

        await this.postAsync(
            Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            }
        );

        cacheStore.UserSettings.PayableInboxFilterSettings = userSettings.PayableInboxFilterSettings;
    }
}
const PayableInboxActions = new Actions(billingStore);

export { PayableInboxActions };
